import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import { adminAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
    Sheet, SheetContent, SheetHeader, SheetTitle
} from '../components/ui/sheet';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuSeparator, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
    Search, Store, Users, Package, ShoppingBag, AlertTriangle,
    MoreVertical, Eye, UserCheck, Ban, CheckCircle, Loader2,
    RefreshCw, TrendingUp, Clock, Building2, MessageSquarePlus,
    Shield, Zap, Activity, CircleDot, Send, BarChart3, CreditCard,
    Coffee, Scissors, ShoppingCart, Wine, Fuel, HelpCircle,
    UtensilsCrossed, AlertCircle, CheckCircle2, XCircle, Timer,
    ChevronRight, User, Mail, Phone, MapPin, CalendarDays
} from 'lucide-react';

// ── Health config ──────────────────────────────────────────────────────────────
const HEALTH_CONFIG = {
    active:      { label: 'Active',       color: 'bg-emerald-500',  text: 'text-emerald-600 dark:text-emerald-400',  bg: 'bg-emerald-500/10 border-emerald-500/30', icon: CheckCircle2,  pulse: true },
    trial:       { label: 'Trial',        color: 'bg-cyan-400',     text: 'text-cyan-600 dark:text-cyan-400',        bg: 'bg-cyan-500/10 border-cyan-500/30',       icon: Timer,         pulse: true },
    idle:        { label: 'Idle',         color: 'bg-amber-400',    text: 'text-amber-600 dark:text-amber-400',      bg: 'bg-amber-500/10 border-amber-500/30',     icon: Clock,         pulse: false },
    expired:     { label: 'Trial Ended',  color: 'bg-orange-500',   text: 'text-orange-600 dark:text-orange-400',   bg: 'bg-orange-500/10 border-orange-500/30',   icon: AlertCircle,   pulse: false },
    suspended:   { label: 'Suspended',    color: 'bg-red-500',      text: 'text-red-600 dark:text-red-400',         bg: 'bg-red-500/10 border-red-500/30',         icon: XCircle,       pulse: false },
    disconnected:{ label: 'Disconnected', color: 'bg-slate-400',    text: 'text-slate-500 dark:text-slate-400',     bg: 'bg-slate-500/10 border-slate-500/30',     icon: AlertTriangle, pulse: false },
    setup:       { label: 'Setting Up',   color: 'bg-violet-400',   text: 'text-violet-600 dark:text-violet-400',   bg: 'bg-violet-500/10 border-violet-500/30',   icon: Zap,           pulse: true },
};

const BUSINESS_ICONS = {
    restaurant: UtensilsCrossed, cafe: Coffee, retail: ShoppingBag,
    liquor: Wine, grocery: ShoppingCart, salon: Scissors,
    convenience: Fuel, other: HelpCircle,
};

const PLAN_COLORS = {
    starter:      'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20',
    professional: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20',
    enterprise:   'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20',
};

const STATUS_FILTERS = [
    { key: 'all',       label: 'All' },
    { key: 'active',    label: 'Active' },
    { key: 'trial',     label: 'Trial' },
    { key: 'idle',      label: 'Idle' },
    { key: 'expired',   label: 'Expired' },
    { key: 'suspended', label: 'Suspended' },
];

function HealthDot({ health, size = 'md' }) {
    const cfg = HEALTH_CONFIG[health] || HEALTH_CONFIG.disconnected;
    const sz = size === 'sm' ? 'w-2 h-2' : 'w-3 h-3';
    return (
        <span className="relative inline-flex items-center justify-center shrink-0">
            <span className={`${sz} rounded-full ${cfg.color}`} />
            {cfg.pulse && (
                <span className={`absolute ${sz} rounded-full ${cfg.color} animate-ping opacity-60`} />
            )}
        </span>
    );
}

function StatCard({ icon: Icon, label, value, sub, color = 'text-cyan-500', bg = 'bg-cyan-500/10' }) {
    return (
        <Card className="shadow-sm border-border">
            <CardContent className="p-5">
                <div className="flex items-start justify-between">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">{label}</p>
                        <p className="text-3xl font-black">{value ?? '—'}</p>
                        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
                    </div>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${bg}`}>
                        <Icon className={`h-5 w-5 ${color}`} />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

export default function AdminPanel() {
    const { user: currentUser, loginAs, logout } = useAuth();
    const [stores, setStores] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedStore, setSelectedStore] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);
    const [newNote, setNewNote] = useState('');
    const [savingNote, setSavingNote] = useState(false);
    const [impersonating, setImpersonating] = useState(null);

    const fetchAll = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        else setRefreshing(true);
        try {
            const [storesRes, statsRes] = await Promise.all([
                adminAPI.listStores(),
                adminAPI.getStats(),
            ]);
            setStores(storesRes.data);
            setStats(statsRes.data);
        } catch { toast.error('Failed to load operator data'); }
        finally { setLoading(false); setRefreshing(false); }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const openDetail = async (store) => {
        setDetailOpen(true);
        setDetailLoading(true);
        setNewNote('');
        try {
            const res = await adminAPI.getStoreDetail(store.id);
            setSelectedStore(res.data);
        } catch { toast.error('Failed to load store details'); }
        finally { setDetailLoading(false); }
    };

    const handleSuspend = async (storeId, e) => {
        e?.stopPropagation();
        if (!window.confirm('Suspend this store? The owner will lose access.')) return;
        try {
            await adminAPI.suspendStore(storeId);
            toast.success('Store suspended');
            fetchAll(true);
            if (selectedStore?.id === storeId) setDetailOpen(false);
        } catch { toast.error('Failed to suspend store'); }
    };

    const handleActivate = async (storeId, e) => {
        e?.stopPropagation();
        try {
            await adminAPI.activateStore(storeId);
            toast.success('Store activated');
            fetchAll(true);
        } catch { toast.error('Failed to activate store'); }
    };

    const handleImpersonate = async (ownerId, storeName, e) => {
        e?.stopPropagation();
        setImpersonating(ownerId);
        try {
            const res = await adminAPI.impersonate(ownerId);
            const prev = localStorage.getItem('token');
            localStorage.setItem('admin_token_backup', prev);
            localStorage.setItem('admin_name_backup', currentUser?.name || '');
            loginAs(res.data);
            toast.success(`Viewing as ${storeName}`);
            window.location.href = '/dashboard';
        } catch { toast.error('Impersonation failed'); }
        finally { setImpersonating(null); }
    };

    const handleAddNote = async () => {
        if (!newNote.trim() || !selectedStore) return;
        setSavingNote(true);
        try {
            const res = await adminAPI.addNote(selectedStore.id, newNote.trim());
            setSelectedStore(prev => ({
                ...prev,
                admin_notes: [res.data.note, ...(prev.admin_notes || [])]
            }));
            setNewNote('');
            toast.success('Note saved');
        } catch { toast.error('Failed to save note'); }
        finally { setSavingNote(false); }
    };

    const filteredStores = stores.filter(s => {
        const q = searchQuery.toLowerCase();
        const matchSearch = !q ||
            s.name?.toLowerCase().includes(q) ||
            s.owner_email?.toLowerCase().includes(q) ||
            s.owner_name?.toLowerCase().includes(q);
        const matchStatus = statusFilter === 'all' || s.system_health === statusFilter;
        return matchSearch && matchStatus;
    });

    const getBusinessIcon = (type) => BUSINESS_ICONS[type] || Building2;

    if (loading) return (
        <Layout title="Operator Dashboard">
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
            </div>
        </Layout>
    );

    return (
        <Layout title="Operator Dashboard">
            <div className="space-y-6 max-w-7xl" data-testid="admin-panel">

                {/* ── PAGE HEADER ── */}
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="flex items-center gap-3 mb-1">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center shadow-md">
                                <Shield className="h-5 w-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-black leading-none">Operator Dashboard</h1>
                                <p className="text-xs text-muted-foreground mt-0.5">Mezgeb POS · Platform Control Center</p>
                            </div>
                        </div>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchAll(true)}
                        disabled={refreshing}
                        className="gap-2"
                    >
                        <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                </div>

                {/* ── STATS ROW ── */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        <StatCard icon={Store}       label="Total Stores"   value={stats.total_stores}       color="text-slate-500"   bg="bg-slate-500/10" />
                        <StatCard icon={CheckCircle2} label="Active"        value={stats.active_stores}      color="text-emerald-500" bg="bg-emerald-500/10" />
                        <StatCard icon={Timer}        label="On Trial"      value={stats.trial_stores}       color="text-cyan-500"    bg="bg-cyan-500/10" />
                        <StatCard icon={AlertCircle}  label="Expiring Soon" value={stats.stores_expiring_soon} color="text-orange-500" bg="bg-orange-500/10" />
                        <StatCard icon={Ban}          label="Suspended"     value={stats.suspended_stores}   color="text-red-500"     bg="bg-red-500/10" />
                        <StatCard icon={Users}        label="Total Users"   value={stats.total_users}        color="text-violet-500"  bg="bg-violet-500/10" />
                    </div>
                )}

                {/* ── FILTERS ── */}
                <div className="flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[200px] max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search by business, owner, email..."
                            className="pl-9 h-9"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {STATUS_FILTERS.map(f => {
                            const cfg = f.key !== 'all' ? HEALTH_CONFIG[f.key] : null;
                            return (
                                <button
                                    key={f.key}
                                    onClick={() => setStatusFilter(f.key)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all border ${
                                        statusFilter === f.key
                                            ? 'bg-slate-950 dark:bg-white text-white dark:text-slate-950 border-transparent shadow-sm'
                                            : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted'
                                    }`}
                                >
                                    {cfg && <span className={`w-2 h-2 rounded-full ${cfg.color}`} />}
                                    {f.label}
                                    <span className="opacity-60">
                                        ({f.key === 'all' ? stores.length : stores.filter(s => s.system_health === f.key).length})
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* ── SUBSCRIBER TABLE ── */}
                <Card className="shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b bg-muted/50">
                                    <th className="text-left px-4 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wide w-8"></th>
                                    <th className="text-left px-4 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wide">Business</th>
                                    <th className="text-left px-4 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wide hidden md:table-cell">Owner</th>
                                    <th className="text-left px-4 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wide">Plan</th>
                                    <th className="text-left px-4 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wide hidden lg:table-cell">Activity</th>
                                    <th className="text-left px-4 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wide hidden xl:table-cell">Trial / Status</th>
                                    <th className="text-left px-4 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wide hidden lg:table-cell">Issues</th>
                                    <th className="w-[50px] px-4 py-3"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {filteredStores.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="py-16 text-center">
                                            <Store className="h-10 w-10 mx-auto mb-3 text-muted-foreground/40" />
                                            <p className="font-semibold text-muted-foreground">No subscribers found</p>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {searchQuery ? 'Try a different search term' : 'No stores registered yet'}
                                            </p>
                                        </td>
                                    </tr>
                                ) : filteredStores.map(store => {
                                    const cfg = HEALTH_CONFIG[store.system_health] || HEALTH_CONFIG.disconnected;
                                    const BizIcon = getBusinessIcon(store.business_type);
                                    const StatusIcon = cfg.icon;
                                    return (
                                        <tr
                                            key={store.id}
                                            className="group hover:bg-muted/30 cursor-pointer transition-colors"
                                            onClick={() => openDetail(store)}
                                            data-testid={`store-row-${store.id}`}
                                        >
                                            {/* Health indicator */}
                                            <td className="px-4 py-3.5">
                                                <HealthDot health={store.system_health} />
                                            </td>

                                            {/* Business */}
                                            <td className="px-4 py-3.5">
                                                <div className="flex items-center gap-2.5">
                                                    <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center shrink-0">
                                                        <BizIcon className="h-4.5 w-4.5 text-muted-foreground" style={{ width: '1.125rem', height: '1.125rem' }} />
                                                    </div>
                                                    <div>
                                                        <p className="font-bold text-sm">{store.name || 'Unnamed'}</p>
                                                        <p className="text-[11px] text-muted-foreground capitalize">{store.business_type || 'Business'}</p>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Owner */}
                                            <td className="px-4 py-3.5 hidden md:table-cell">
                                                <p className="font-medium text-sm">{store.owner_name || '—'}</p>
                                                <p className="text-[11px] text-muted-foreground">{store.owner_email}</p>
                                            </td>

                                            {/* Plan */}
                                            <td className="px-4 py-3.5">
                                                <Badge
                                                    variant="outline"
                                                    className={`capitalize text-xs ${PLAN_COLORS[store.subscription_plan] || PLAN_COLORS.starter}`}
                                                >
                                                    {store.subscription_plan || 'starter'}
                                                </Badge>
                                            </td>

                                            {/* Activity */}
                                            <td className="px-4 py-3.5 hidden lg:table-cell">
                                                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                                    <span className="flex items-center gap-1">
                                                        <Users className="h-3 w-3" />{store.user_count}
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <Package className="h-3 w-3" />{store.product_count}
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <BarChart3 className="h-3 w-3" />{store.sales_last_30d}
                                                        <span className="text-[10px] opacity-60">30d</span>
                                                    </span>
                                                </div>
                                            </td>

                                            {/* Trial / Status */}
                                            <td className="px-4 py-3.5 hidden xl:table-cell">
                                                <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold border ${cfg.bg}`}>
                                                    <StatusIcon className={`h-3 w-3 ${cfg.text}`} />
                                                    <span className={cfg.text}>{cfg.label}</span>
                                                    {store.trial_days_remaining !== null && store.system_health === 'trial' && (
                                                        <span className={`font-mono font-black ${cfg.text}`}>{store.trial_days_remaining}d</span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Issues */}
                                            <td className="px-4 py-3.5 hidden lg:table-cell">
                                                {store.open_issues > 0 ? (
                                                    <Badge className="bg-red-500/10 text-red-600 border-red-500/20 text-xs gap-1" variant="outline">
                                                        <AlertTriangle className="h-3 w-3" />{store.open_issues}
                                                    </Badge>
                                                ) : (
                                                    <span className="text-xs text-muted-foreground">—</span>
                                                )}
                                            </td>

                                            {/* Actions */}
                                            <td className="px-4 py-3.5" onClick={e => e.stopPropagation()}>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100">
                                                            <MoreVertical className="h-4 w-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="w-48">
                                                        <DropdownMenuItem onClick={() => openDetail(store)} className="gap-2">
                                                            <Eye className="h-4 w-4" /> View Details
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem
                                                            onClick={e => handleImpersonate(store.owner_id, store.name, e)}
                                                            disabled={!!impersonating}
                                                            className="gap-2"
                                                        >
                                                            {impersonating === store.owner_id
                                                                ? <Loader2 className="h-4 w-4 animate-spin" />
                                                                : <UserCheck className="h-4 w-4" />}
                                                            Access as Owner
                                                        </DropdownMenuItem>
                                                        <DropdownMenuSeparator />
                                                        {store.system_health === 'suspended' ? (
                                                            <DropdownMenuItem onClick={e => handleActivate(store.id, e)} className="gap-2 text-emerald-600">
                                                                <CheckCircle className="h-4 w-4" /> Activate Store
                                                            </DropdownMenuItem>
                                                        ) : (
                                                            <DropdownMenuItem onClick={e => handleSuspend(store.id, e)} className="gap-2 text-destructive">
                                                                <Ban className="h-4 w-4" /> Suspend Store
                                                            </DropdownMenuItem>
                                                        )}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {filteredStores.length > 0 && (
                        <div className="px-4 py-2.5 border-t bg-muted/20 text-xs text-muted-foreground">
                            Showing {filteredStores.length} of {stores.length} subscribers
                        </div>
                    )}
                </Card>
            </div>

            {/* ── STORE DETAIL SHEET ── */}
            <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
                <SheetContent side="right" className="w-full sm:max-w-2xl p-0 flex flex-col">
                    {detailLoading ? (
                        <div className="flex-1 flex items-center justify-center">
                            <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
                        </div>
                    ) : selectedStore ? (
                        <>
                            {/* Sheet Header */}
                            <div className="bg-slate-950 dark:bg-slate-900 text-white p-6 shrink-0">
                                <SheetHeader className="space-y-0">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                                                {(() => {
                                                    const BizIcon = getBusinessIcon(selectedStore.business_type);
                                                    return <BizIcon className="h-6 w-6 text-white" />;
                                                })()}
                                            </div>
                                            <div>
                                                <SheetTitle className="text-white font-black text-xl">{selectedStore.name}</SheetTitle>
                                                <p className="text-slate-400 text-sm capitalize mt-0.5">{selectedStore.business_type || 'Business'}</p>
                                            </div>
                                        </div>
                                        <div>
                                            {(() => {
                                                const cfg = HEALTH_CONFIG[selectedStore.system_health] || HEALTH_CONFIG.disconnected;
                                                const StatusIcon = cfg.icon;
                                                return (
                                                    <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border ${cfg.bg}`}>
                                                        <HealthDot health={selectedStore.system_health} size="sm" />
                                                        <span className={cfg.text}>{cfg.label}</span>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>

                                    {/* Quick stats */}
                                    <div className="grid grid-cols-4 gap-3 mt-5">
                                        {[
                                            { icon: Users, value: selectedStore.users?.length || 0, label: 'Users' },
                                            { icon: Package, value: selectedStore.product_count || 0, label: 'Products' },
                                            { icon: BarChart3, value: selectedStore.sales_last_30d || 0, label: '30d Sales' },
                                            { icon: AlertTriangle, value: selectedStore.issues?.length || 0, label: 'Issues' },
                                        ].map(({ icon: Icon, value, label }) => (
                                            <div key={label} className="text-center bg-white/5 rounded-xl p-3">
                                                <p className="text-2xl font-black text-white">{value}</p>
                                                <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wide mt-0.5">{label}</p>
                                            </div>
                                        ))}
                                    </div>
                                </SheetHeader>
                            </div>

                            <ScrollArea className="flex-1">
                                <div className="p-6 space-y-6">

                                    {/* Business Info */}
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">Business Info</p>
                                        <Card className="shadow-sm">
                                            <CardContent className="p-4 space-y-3">
                                                <div className="grid sm:grid-cols-2 gap-3">
                                                    <InfoRow icon={User} label="Owner" value={selectedStore.owner_name || '—'} />
                                                    <InfoRow icon={Mail} label="Email" value={selectedStore.owner_email || '—'} />
                                                    {selectedStore.phone && <InfoRow icon={Phone} label="Phone" value={selectedStore.phone} />}
                                                    {selectedStore.address && <InfoRow icon={MapPin} label="Address" value={selectedStore.address} />}
                                                    <InfoRow icon={CalendarDays} label="Joined" value={new Date(selectedStore.created_at).toLocaleDateString()} />
                                                    {selectedStore.last_sale_at && (
                                                        <InfoRow icon={Activity} label="Last Sale" value={new Date(selectedStore.last_sale_at).toLocaleDateString()} />
                                                    )}
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </div>

                                    {/* Subscription */}
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">Subscription</p>
                                        <Card className="shadow-sm">
                                            <CardContent className="p-4">
                                                <div className="grid sm:grid-cols-2 gap-3">
                                                    <InfoRow icon={CreditCard} label="Plan"
                                                        value={<Badge variant="outline" className={`capitalize text-xs ${PLAN_COLORS[selectedStore.subscription_plan] || PLAN_COLORS.starter}`}>{selectedStore.subscription_plan || 'starter'}</Badge>}
                                                    />
                                                    <InfoRow icon={Shield} label="Status"
                                                        value={<span className="capitalize font-semibold">{selectedStore.subscription_status || '—'}</span>}
                                                    />
                                                    {selectedStore.trial_end && (
                                                        <InfoRow icon={Timer} label="Trial Ends"
                                                            value={
                                                                <span className={selectedStore.trial_days_remaining === 0 ? 'text-red-500 font-bold' : 'font-semibold'}>
                                                                    {new Date(selectedStore.trial_end).toLocaleDateString()}
                                                                    {selectedStore.trial_days_remaining !== null && (
                                                                        <span className="text-muted-foreground font-normal ml-1">
                                                                            ({selectedStore.trial_days_remaining === 0 ? 'Expired' : `${selectedStore.trial_days_remaining}d left`})
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            }
                                                        />
                                                    )}
                                                </div>
                                                <div className="flex gap-2 mt-4">
                                                    {selectedStore.subscription_status === 'suspended' ? (
                                                        <Button size="sm" onClick={() => handleActivate(selectedStore.id)} className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2">
                                                            <CheckCircle className="h-4 w-4" /> Activate Store
                                                        </Button>
                                                    ) : (
                                                        <Button size="sm" variant="destructive" onClick={() => handleSuspend(selectedStore.id)} className="gap-2">
                                                            <Ban className="h-4 w-4" /> Suspend Store
                                                        </Button>
                                                    )}
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        disabled={!!impersonating}
                                                        onClick={e => handleImpersonate(selectedStore.owner_id, selectedStore.name, e)}
                                                        className="gap-2"
                                                    >
                                                        {impersonating === selectedStore.owner_id
                                                            ? <Loader2 className="h-4 w-4 animate-spin" />
                                                            : <UserCheck className="h-4 w-4 text-cyan-500" />}
                                                        Access as Owner
                                                    </Button>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </div>

                                    {/* Staff List */}
                                    {selectedStore.users?.length > 0 && (
                                        <div>
                                            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
                                                Staff ({selectedStore.users.length})
                                            </p>
                                            <Card className="shadow-sm">
                                                <CardContent className="p-0">
                                                    <div className="divide-y divide-border">
                                                        {selectedStore.users.map(u => (
                                                            <div key={u.id} className="flex items-center justify-between px-4 py-3">
                                                                <div className="flex items-center gap-2.5">
                                                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center">
                                                                        <span className="text-[10px] font-black text-white">{u.name?.charAt(0)?.toUpperCase()}</span>
                                                                    </div>
                                                                    <div>
                                                                        <p className="font-semibold text-sm">{u.name}</p>
                                                                        <p className="text-[11px] text-muted-foreground">{u.email}</p>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    {u.job_title && <span className="text-xs text-muted-foreground">{u.job_title}</span>}
                                                                    <Badge variant="outline" className="capitalize text-xs">{u.role}</Badge>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        </div>
                                    )}

                                    {/* Recent Sales */}
                                    {selectedStore.recent_sales?.length > 0 && (
                                        <div>
                                            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">Recent Sales</p>
                                            <Card className="shadow-sm">
                                                <CardContent className="p-0">
                                                    <div className="divide-y divide-border">
                                                        {selectedStore.recent_sales.map(sale => (
                                                            <div key={sale.id} className="flex items-center justify-between px-4 py-3 text-sm">
                                                                <div>
                                                                    <p className="font-semibold font-mono">${sale.total?.toFixed(2)}</p>
                                                                    <p className="text-[11px] text-muted-foreground">{new Date(sale.created_at).toLocaleString()}</p>
                                                                </div>
                                                                <Badge variant="outline" className="capitalize text-xs">{sale.payment_method}</Badge>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        </div>
                                    )}

                                    {/* Admin Notes */}
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">Support Notes</p>
                                        <Card className="shadow-sm">
                                            <CardContent className="p-4 space-y-3">
                                                <div className="flex gap-2">
                                                    <Input
                                                        placeholder="Add a support note or issue log..."
                                                        value={newNote}
                                                        onChange={e => setNewNote(e.target.value)}
                                                        onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleAddNote()}
                                                        className="h-9 text-sm"
                                                    />
                                                    <Button
                                                        size="sm"
                                                        onClick={handleAddNote}
                                                        disabled={savingNote || !newNote.trim()}
                                                        className="bg-cyan-500 hover:bg-cyan-600 text-white shrink-0"
                                                    >
                                                        {savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                                    </Button>
                                                </div>
                                                {selectedStore.admin_notes?.length > 0 ? (
                                                    <div className="space-y-2 max-h-52 overflow-y-auto">
                                                        {selectedStore.admin_notes.map(note => (
                                                            <div key={note.id} className="rounded-xl bg-muted/50 border border-border p-3">
                                                                <p className="text-sm">{note.note}</p>
                                                                <div className="flex items-center gap-2 mt-1.5">
                                                                    <p className="text-[10px] text-muted-foreground">{note.admin_email}</p>
                                                                    <span className="text-muted-foreground text-[10px]">·</span>
                                                                    <p className="text-[10px] text-muted-foreground">{new Date(note.created_at).toLocaleString()}</p>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="text-xs text-muted-foreground text-center py-3">No notes yet</p>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </div>
                                </div>
                            </ScrollArea>
                        </>
                    ) : null}
                </SheetContent>
            </Sheet>
        </Layout>
    );
}

function InfoRow({ icon: Icon, label, value }) {
    return (
        <div className="flex items-start gap-2.5">
            <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
                <div className="text-sm font-medium">{value}</div>
            </div>
        </div>
    );
}
